import * as api from '@owl-nest/api-client/next'
import { Either } from '@owl-nest/monad'

export async function cancel(slugOrId: string | number): Promise<api.HttpResult<api.Project>> {
  const response = await api.client('POST /projects/:id/cancel', { urlParams: { id: String(slugOrId) } })
  return response.next((success) => success.body)
}

export async function end(slugOrId: string | number): Promise<api.HttpResult<api.Project>> {
  const response = await api.client('POST /projects/:id/end', { urlParams: { id: String(slugOrId) } })
  return response.next((success) => success.body)
}

export async function like(slugOrId: string | number): Promise<api.HttpResponse<void>> {
  const response = await api.client('POST /projects/:id/like', { urlParams: { id: String(slugOrId) } })
  return response
}

export async function unlike(slugOrId: string | number): Promise<api.HttpResponse<void>> {
  const response = await api.client('POST /projects/:id/unlike', { urlParams: { id: String(slugOrId) } })
  return response
}

export async function publish<EXTRA_FIELDS extends string>(
  slugOrId: string | number,
  payload: api.ProjectPublishRequest,
): Promise<api.HttpResult<api.Project<EXTRA_FIELDS>>> {
  const response = await api.client('POST /projects/:id/publish', {
    urlParams: { id: String(slugOrId) },
    body: payload,
  })

  return response.next((success) => success.body as api.Project<EXTRA_FIELDS>)
}

export async function computeExamples(
  slugOrId: string | number,
  config: api.ProjectExamplesRequest,
): Promise<api.HttpResult<api.ProjectExamplesResponse | undefined>> {
  const response = await api.client('POST /projects/:id/compute-examples', {
    urlParams: { id: String(slugOrId) },
    body: config,
  })

  return response.next((success) => success.body)
}

export type LoadConfig<EXTRA_FIELDS extends string> = {
  accessToken?: string
  extraFields?: readonly EXTRA_FIELDS[]
  identifier?: string
  projectToken?: string
  withToken?: boolean
  fetchOnMount?: boolean | 'force'
}

export async function load<EXTRA_FIELDS extends string>(
  slugOrId: string | number,
  { accessToken, withToken = false, projectToken, identifier, extraFields = [] }: LoadConfig<EXTRA_FIELDS> = {},
): Promise<api.HttpResult<api.Project<EXTRA_FIELDS>>> {
  const getParams: Record<string, string> = {
    extra_fields: extraFields.join(','),
  }

  if (projectToken) {
    getParams['token'] = projectToken
  }

  if (identifier) {
    getParams['identifier'] = identifier
  }

  const response = await api.client('GET /projects/:id', {
    getParams,
    token: accessToken,
    urlParams: { id: String(slugOrId) },
    withToken,
  })

  return response.next((success) => success.body as api.Project<EXTRA_FIELDS>)
}

export async function submit(
  slugOrId: string | number,
  config: api.ProjectSubmitRequest,
): Promise<api.HttpResult<api.Project>> {
  const response = await api.client('POST /projects/:id/submit', {
    urlParams: { id: String(slugOrId) },
    getParams: {
      extra_fields: 'user_role',
    },
    body: config,
  })

  return response.next((success) => success.body)
}

type UpdateConfig<EXTRA_FIELDS extends string> = {
  extraFields?: readonly EXTRA_FIELDS[]
}

export async function update<EXTRA_FIELDS extends string = ''>(
  slugOrId: string | number,
  update: api.UpdateProject<EXTRA_FIELDS>,
  { extraFields = [] }: UpdateConfig<EXTRA_FIELDS> = {},
): Promise<api.HttpResult<api.Project<EXTRA_FIELDS>>> {
  const response = await api.client('PATCH /projects/:id', {
    urlParams: { id: String(slugOrId) },
    getParams: {
      extra_fields: extraFields.join(','),
    },
    body: update,
  })

  return response.next((success) => success.body as api.Project<EXTRA_FIELDS>)
}

export async function deleteSlug(id: number): Promise<api.HttpResponse<void>> {
  const response = await api.client('DELETE /slugs/:id', {
    urlParams: {
      id: String(id),
    },
  })

  return response
}

type ImageUploadOptions = {
  file: File
  lang: api.Lang
  type?: api.ImageType
}

export async function uploadImage(
  slugOrId: string | number,
  { lang, file, type = api.ImageType.SECONDARY }: ImageUploadOptions,
): Promise<api.HttpResult<api.ImageResource>> {
  const result = await api.client('POST /projects/:id/images', {
    urlParams: {
      id: String(slugOrId),
    },
    body: { image: file, lang, type },
  })

  return result.caseOf({
    left: (failure) => {
      return Either.left(failure)
    },
    right: (success) => {
      return Either.right(success.body)
    },
  })
}

export function comment(slugOrId: string | number, comment: string) {
  return api.client('POST /projects/:id/comments', {
    urlParams: { id: String(slugOrId) },
    body: { comment },
  })
}

export async function refreshSocials(slugOrId: string | number, provider: api.UserSocialProvider) {
  const response = await api.client('POST /projects/:id/refresh/:provider', {
    urlParams: { id: String(slugOrId), provider },
  })
  return response.next((success) => success.body)
}

export async function rewards(slugOrId: string | number) {
  const response = await api.client('GET /projects/:id/rewards', {
    urlParams: { id: String(slugOrId) },
  })

  return response.next((success) => success.body)
}

export async function createReward(
  slugOrId: string | number,
  payload: api.CreateReward,
): Promise<api.HttpResult<api.Reward>> {
  const response = await api.client('POST /projects/:id/rewards', {
    urlParams: { id: String(slugOrId) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function updateReward(rewardId: number, payload: api.UpdateReward): Promise<api.HttpResult<api.Reward>> {
  const response = await api.client('PATCH /rewards/:id', {
    urlParams: { id: String(rewardId) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function deleteReward(rewardId: number): Promise<api.HttpResult<void>> {
  const response = await api.client('DELETE /rewards/:id', {
    urlParams: { id: String(rewardId) },
  })

  return response.next((success) => success.body)
}

export async function rewardVariants(
  rewardId: number | string,
  valueIds?: string[],
  withToken = false,
): Promise<
  api.HttpResult<{
    variants: api.Variant[]
  }>
> {
  const response = await api.client('GET /rewards/:id/variants', {
    getParams: { ...(valueIds && { value_ids: valueIds.join(',') }) },
    urlParams: { id: String(rewardId) },
    withToken,
  })

  return response.next((success) => success.body)
}

export async function createShippingZone(
  slugOrId: string | number,
  payload: api.CreateShippingZone,
): Promise<api.HttpResult<api.ShippingZones>> {
  const response = await api.client('POST /projects/:id/shipping-zones', {
    urlParams: { id: String(slugOrId) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function updateShippingZone(
  shippingZoneId: number,
  payload: api.UpdateShippingZone,
): Promise<api.HttpResult<api.ShippingZones>> {
  const response = await api.client('PATCH /shipping-zones/:id', {
    urlParams: { id: String(shippingZoneId) },
    body: payload,
  })

  return response.next((success) => success.body)
}

export async function deleteShippingZone(shippingZoneId: number): Promise<api.HttpResult<void>> {
  const response = await api.client('DELETE /shipping-zones/:id', {
    urlParams: { id: String(shippingZoneId) },
  })

  return response.next((success) => success.body)
}
